import {memo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {itemShape} from '../shapes';

function getLocalDateStr(date) {
    return date.toLocaleDateString('en-CA'); // "YYYY-MM-DD" in local timezone
}

function formatDueDate(dueDate) {
    const today = getLocalDateStr(new Date());
    const tomorrow = getLocalDateStr(new Date(Date.now() + 86400000));
    if (dueDate === today) return 'Today';
    if (dueDate === tomorrow) return 'Tomorrow';
    // Format as "Mar 22" style
    const [year, month, day] = dueDate.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-US', {month: 'short', day: 'numeric'});
}

const ChecklistItem = memo(function ChecklistItem({checklistId, item, onToggle, onDelete, onUpdate, onStar, onSetDueDate}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const dateInputRef = useRef(null);

    function startEditing() {
        setDraft(item.title);
        setEditing(true);
    }

    function commit() {
        const title = draft.trim();
        if (title && title !== item.title) onUpdate(checklistId, item.id, title);
        setEditing(false);
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') setEditing(false);
    }

    function handleCalendarClick() {
        dateInputRef.current?.showPicker?.();
        dateInputRef.current?.click();
    }

    function handleDateChange(e) {
        const value = e.target.value; // "YYYY-MM-DD" or "" if cleared
        onSetDueDate(checklistId, item.id, value || null);
    }

    const today = getLocalDateStr(new Date());
    const isOverdue = !item.checked && item.dueDate && item.dueDate < today;

    const className = [
        'checklist-item',
        item.starred ? 'checklist-item--starred' : '',
        isOverdue ? 'checklist-item--overdue' : '',
    ].filter(Boolean).join(' ');

    return (
        <div className={className}>
            <span className="checklist-item-drag-handle" aria-hidden="true">
                <svg width="10" height="14" viewBox="0 0 10 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="3" cy="2.5" r="1.2" fill="currentColor"/>
                    <circle cx="7" cy="2.5" r="1.2" fill="currentColor"/>
                    <circle cx="3" cy="7" r="1.2" fill="currentColor"/>
                    <circle cx="7" cy="7" r="1.2" fill="currentColor"/>
                    <circle cx="3" cy="11.5" r="1.2" fill="currentColor"/>
                    <circle cx="7" cy="11.5" r="1.2" fill="currentColor"/>
                </svg>
            </span>
            <input
                type="checkbox"
                checked={item.checked}
                onChange={() => onToggle(checklistId, item.id)}
                onTouchEnd={(e) => { e.preventDefault(); onToggle(checklistId, item.id); }}
            />
            {editing
                ? <input
                    className="checklist-item-title-input"
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onBlur={commit}
                    onKeyDown={handleKeyDown}
                    autoFocus
                  />
                : <span
                    className="checklist-item-label"
                    onClick={startEditing}
                    onKeyDown={e => e.key === 'Enter' && startEditing()}
                    role="button"
                    tabIndex={0}
                  >{item.title}</span>
            }
            {item.dueDate && (
                <span className={`checklist-item-due-date${isOverdue ? ' checklist-item-due-date--overdue' : ''}`}>
                    {formatDueDate(item.dueDate)}
                </span>
            )}
            <div className="checklist-item-calendar">
                <button
                    className="checklist-item-calendar-btn"
                    onClick={handleCalendarClick}
                    aria-label={item.dueDate ? 'Change due date' : 'Set due date'}
                >
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="1" y="2" width="10" height="9" rx="1" stroke="currentColor" strokeWidth="1.3"/>
                        <line x1="1" y1="5" x2="11" y2="5" stroke="currentColor" strokeWidth="1.3"/>
                        <line x1="4" y1="1" x2="4" y2="3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                        <line x1="8" y1="1" x2="8" y2="3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                    </svg>
                </button>
                <input
                    ref={dateInputRef}
                    type="date"
                    className="checklist-item-date-input"
                    value={item.dueDate || ''}
                    onChange={handleDateChange}
                    aria-label="Due date"
                />
            </div>
            <button
                className={`checklist-item-star${item.starred ? ' checklist-item-star--active' : ''}`}
                onClick={() => onStar(checklistId, item.id)}
                aria-label={item.starred ? 'Unstar item' : 'Star item'}
            >
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <polygon points="6,1 7.5,4.5 11.5,4.5 8.5,7 9.5,11 6,8.5 2.5,11 3.5,7 0.5,4.5 4.5,4.5"
                        stroke="currentColor" strokeWidth="1" strokeLinejoin="round"
                        fill={item.starred ? 'currentColor' : 'none'}/>
                </svg>
            </button>
            <button
                className="checklist-item-delete"
                onClick={() => onDelete(checklistId, item.id)}
                aria-label="Delete item"
            >
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <polyline points="1,3 11,3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    <path d="M4,3 L4,2 Q4,1 5,1 L7,1 Q8,1 8,2 L8,3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2.5,3 L3,10 Q3,11 4,11 L8,11 Q9,11 9,10 L9.5,3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
        </div>
    );
});

ChecklistItem.propTypes = {
    checklistId: PropTypes.string.isRequired,
    item: itemShape.isRequired,
    onToggle: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
    onStar: PropTypes.func.isRequired,
    onSetDueDate: PropTypes.func.isRequired,
};

export default ChecklistItem;
